import {
  useDeleteDiscount,
  useGenerateOrderSlip,
  usePostOrder,
} from '@api/pos/pos-transaction-api.rtk';
import DrawerWrapper from '@components/drawer/drawer-wrapper';
import useAppContext from '@hooks/useAppContext';
import { ConfirmationDialog } from '@hooks/useConfirmationDialog';
import useDisclosure, { UseDisclosureInterface } from '@hooks/useDisclosure';
import useToast from '@hooks/useToast';
import {
  selectPOSstationID,
  selectPOSTableDetails,
  selectPOSTableOrders,
  selectPOSTableTransactionID,
} from '@modules/point-of-sale/slice/pos-selector';
import { Stack, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { useAppSelector } from '@store/index';
import { selectPropertyID } from '@store/selector/property.selector';
import {
  Icon,
  IconDiscountOff,
  IconMapPinMinus,
  IconPrinter,
  IconProps,
  IconReplace,
} from '@tabler/icons-react';
import { buildPosOrderPayload } from '@utils/pos-utils';
import { hashSignature } from '@utils/security';
import {
  ForwardRefExoticComponent,
  RefAttributes,
  useMemo,
  useState,
} from 'react';
import POSChargeToAccountModal from '../others/pos-charge-to-account.modal';
import POSChargeToRoomModal from '../others/pos-charge-to-room.modal';
import POSReprintVoidModal from '../others/pos-reprint-void.modal';
import POSSplitBillModal from '../others/pos-split-bill.modal';
import POSVoidDiscountModal from '../others/pos-void-discount.modal';
import { usePOSWindowDrawerCtx } from '../pos-order-taking-window';

interface IPOSOthersButtons {
  onClick: () => void | Promise<void>;
  title: string;
  icon: ForwardRefExoticComponent<IconProps & RefAttributes<Icon>>;
  disabled?: boolean;
}

const POSOthersDrawer = ({
  isMisc,
  modalDisclosure,
}: {
  isMisc?: boolean;
  modalDisclosure?: UseDisclosureInterface;
}) => {
  // #region STATE
  const orders = useAppSelector(selectPOSTableOrders);
  const stationID = useAppSelector(selectPOSstationID);
  const transactionID = useAppSelector(selectPOSTableTransactionID);
  const propertyID = useAppSelector(selectPropertyID);
  const transactionDetails = useAppSelector(selectPOSTableDetails);
  const ctx = !isMisc ? usePOSWindowDrawerCtx() : null;
  const { toast } = useToast();

  const isOpen = isMisc
    ? modalDisclosure?.isOpen
    : ctx?.othersDrawerDisclosure.isOpen;

  const onClose = isMisc
    ? modalDisclosure?.onClose
    : ctx?.othersDrawerDisclosure.onClose;

  const tableDrawerDisclosure = isMisc
    ? useDisclosure()
    : ctx?.tableDrawerDisclosure;

  const tableTransferDisclosure = isMisc
    ? useDisclosure()
    : ctx?.tableTransferDisclosure;

  const {
    openSecCodeDialog,
    // startLoadingStatus,
    stopLoadingStatus,
    genpar: { getGenparValue },
  } = useAppContext();

  const isPOSFOSIntegration = getGenparValue('pos_fos_integration') === 'true';

  const disclosures = {
    splitBillModal: useDisclosure(),
    chargeToRoomModal: useDisclosure(),
    chargeToAccountModal: useDisclosure(),
    rePrintModal: useDisclosure(),
    voidDiscountModal: useDisclosure(),
  };

  const {
    splitBillModal,
    chargeToRoomModal,
    chargeToAccountModal,
    rePrintModal,
    voidDiscountModal,
  } = disclosures;

  const closeAllDisclosures = () => {
    Object.values(disclosures).forEach((disclosure) => {
      if (disclosure.onClose) {
        disclosure.onClose();
      }
    });
  };

  const handleDrawerClose = () => {
    if (isMisc) {
      modalDisclosure?.onClose();
    }
    onClose?.();
    closeAllDisclosures();
  };
  // #endregion

  // #region VOID DISCOUNT
  const [voidDialog, setVoidDialog] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const { hasNewItem, noOrders } = useMemo(() => {
    const hasNewItem = orders?.some(
      (item) => !item.orderID || !item.isPrintedOS,
    );

    const noOrders = orders?.length <= 0;

    return { hasNewItem, noOrders };
  }, [orders]);

  const [deleteDiscount] = useDeleteDiscount();
  const [postOrder] = usePostOrder();
  const [generateOrderSlip] = useGenerateOrderSlip();

  const handleVoid = async () => {
    await openSecCodeDialog(async (res) => {
      try {
        if (!res?.data.isValid) return;

        await deleteDiscount({
          transactionID,
          payload: {
            properties: [
              {
                property: {
                  propertyID,
                  voidDiscount: {
                    discountBy: 'All',
                    voidAll: true,
                    signature: hashSignature(`${propertyID}1`),
                  },
                },
              },
            ],
          },
        }).unwrap();
      } catch (error) {
        toast({ message: error, severity: 'warning' });
      } finally {
        setVoidDialog(false);
        handleDrawerClose();
      }
    }, 'void_discount');
  };

  const handleSyncOrder = async () => {
    try {
      // startLoadingStatus(`Syncing ${orders.length > 0 ? 'Orders' : 'Order'}`);

      await postOrder({
        transactionID,
        payload: {
          properties: [
            {
              property: {
                propertyID,
                posOrders: buildPosOrderPayload({
                  orders:
                    orders?.filter(
                      (item) => !item.isPrintedOS && !item.orderID,
                    ) ?? [],
                  propertyID,
                  stationID,
                }),
              },
            },
          ],
        },
      }).unwrap();

      // startLoadingStatus('Generating Order Slip');

      generateOrderSlip({
        transactionID,
        payload: {
          properties: [
            {
              property: {
                propertyID,
                signature: hashSignature(String(propertyID)),
              },
            },
          ],
        },
      }).unwrap();

      toast({
        message: 'Order slip printed successfully',
        severity: 'success',
      });
    } catch (error) {
      toast({ message: error, severity: 'warning' });
    } finally {
      stopLoadingStatus();
      chargeToRoomModal.onOpen();
      setShowDialog(false);
    }
  };

  // #endregion

  // #region BUTTONS
  const buttons: IPOSOthersButtons[] = [
    // {
    //   title: 'Split Bill',
    //   icon: IconScissors,
    //   onClick: splitBillModal.onOpen,
    //   disabled: true,
    // },
    {
      title: 'Charge to Room',
      icon: IconMapPinMinus,
      onClick: () => {
        if (!isPOSFOSIntegration) {
          toast({
            message: `<span style="padding-bottom: 8px"><b>Feature Not Available</b></span> <br/>
<span>This feature requires Xenia Cloud Front Office System. Make sure that your license is active and the integration is enabled.</span>
`,
            severity: 'warning',
          });
          return;
        }
        openSecCodeDialog(async (res) => {
          if (!res?.data.isValid) return;
          hasNewItem ? setShowDialog(true) : chargeToRoomModal.onOpen();
        }, 'charge_to_room');
      },
      disabled: noOrders,
    },
    // {
    //   disabled: true,
    //   title: 'Charge to Account',
    //   icon: IconUserMinus,
    //   onClick: () => {
    //     openSecCodeDialog(async (res) => {
    //       if (!res?.data.isValid) return;
    //       chargeToAccountModal.onOpen();
    //     }, 'charge_to_account');
    //   },
    // },
    // { title: 'Add %', icon: IconFilePercent, onClick: onClose, disabled: true },
    {
      title: 'Transfer Table',
      icon: IconReplace,
      onClick: () => {
        tableTransferDisclosure?.onOpen();
        tableDrawerDisclosure?.onOpen();
      },
    },
    // {
    //   title: 'Override Price',
    //   icon: IconTags,
    //   onClick: onClose,
    //   disabled: true,
    // },
    // {
    //   title: 'Search Guest',
    //   icon: IconMoodSearch,
    //   onClick: onClose,
    //   disabled: true,
    // },
    // {
    //   title: 'Loyalty Card',
    //   icon: IconGiftCard,
    //   onClick: onClose,
    //   disabled: true,
    // },
    // {
    //   title: 'Item Availability',
    //   icon: IconPaperBag,
    //   onClick: onClose,
    //   disabled: true,
    // },
    { title: 'Reprint', icon: IconPrinter, onClick: rePrintModal.onOpen },
    {
      title: transactionDetails?.isGroupComputation
        ? 'Void All Discount'
        : 'Void Discount',
      icon: IconDiscountOff,
      onClick: transactionDetails?.isGroupComputation
        ? () => setVoidDialog(true)
        : voidDiscountModal.onOpen,
      disabled: noOrders,
    },
  ];

  const miscButtons: IPOSOthersButtons[] = [
    { title: 'Reprint', icon: IconPrinter, onClick: rePrintModal.onOpen },
  ];
  // #endregion

  return (
    <>
      <DrawerWrapper
        {...{
          open: isOpen,
          onClose: handleDrawerClose,
          withHeader: {
            title: isMisc ? 'Misc' : 'Others',
            learnMore: () => {},
            sx: {
              maxWidth: '800px',
              minWidth: { xs: '100%', sm: '500px' },
            },
          },
        }}
      >
        <Stack sx={{ p: 3 }}>
          <Grid2 container spacing={2}>
            {[...(isMisc ? miscButtons : buttons)]
              .sort((a, b) => a.title.localeCompare(b.title))
              .map((button, i) => (
                <Grid2 key={button.title + i} {...{ xs: 12, sm: 6 }}>
                  <Stack
                    gap={2}
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: '130px',
                      cursor: button.disabled
                        ? 'not-allowed'
                        : button.title === 'Charge to Room' &&
                            !isPOSFOSIntegration
                          ? 'normal'
                          : 'pointer',
                      borderRadius: '12px',
                      border: '1px solid #EEE',
                      bgcolor:
                        button.disabled ||
                        (button.title === 'Charge to Room' &&
                          !isPOSFOSIntegration)
                          ? '#F5F5F5'
                          : '#FFF',
                      opacity:
                        button.disabled ||
                        (button.title === 'Charge to Room' &&
                          !isPOSFOSIntegration)
                          ? 0.5
                          : 1,
                      ':hover': button.disabled
                        ? {}
                        : {
                            bgcolor: 'primary.100',
                          },
                    }}
                    onClick={button.disabled ? undefined : button.onClick}
                  >
                    <button.icon stroke={1.2} size={35} color='#354052' />
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontWeight: 500,
                        fontFamily: 'Montserrat',
                      }}
                    >
                      {button.title}
                    </Typography>
                  </Stack>
                </Grid2>
              ))}
          </Grid2>
        </Stack>
      </DrawerWrapper>

      <POSSplitBillModal {...{ modalDisclosure: splitBillModal }} />
      <POSChargeToRoomModal {...{ modalDisclosure: chargeToRoomModal }} />
      <POSChargeToAccountModal {...{ modalDisclosure: chargeToAccountModal }} />
      <POSReprintVoidModal
        {...{ modalDisclosure: rePrintModal, filterByTransaction: !isMisc }}
      />
      <POSVoidDiscountModal {...{ modalDisclosure: voidDiscountModal }} />

      <ConfirmationDialog
        dialogStatus='warning'
        iconColor='warning'
        open={voidDialog}
        title={'Void Discount'}
        desc={
          'This transaction uses group computation. Do you want to void the discount?'
        }
        onConfirm={handleVoid}
        onClose={() => setVoidDialog(false)}
      />

      <ConfirmationDialog
        dialogStatus='warning'
        iconColor='warning'
        open={showDialog}
        title={`Pending OS for Printing`}
        desc={`Are you sure you want to bill out this transaction?\nThe order slip will be printed automatically.`}
        onConfirm={handleSyncOrder}
        onClose={() => setShowDialog(false)}
      />
    </>
  );
};

export default POSOthersDrawer;
